import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { affiliationAPI, orgAPI } from '../../services/api';
import { Loader2, Users, FileText, CheckCircle2, CreditCard, Search, AlertTriangle } from 'lucide-react';

const ESTADO_BADGE = {
  activo: 'bg-green-100 text-green-800',
  por_renovar: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-gray-100 text-gray-800',
  vencido: 'bg-red-100 text-red-800',
};

// Panel canónico de afiliaciones (nacional y territorial). Ruta única: /admin/afiliaciones
const AdminAfiliaciones = () => {
  const [ctx, setCtx] = useState(null);
  const [seccion, setSeccion] = useState('');
  const [tablero, setTablero] = useState(null);
  const [bucket, setBucket] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [check, setCheck] = useState(null);
  const [docs, setDocs] = useState([]);
  const [directorio, setDirectorio] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagos, setPagos] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [cargando, setCargando] = useState(true);
  const { toast } = useToast();

  const err = (e) => toast({ title: 'Error', description: e.message || 'Operación rechazada', variant: 'destructive' });

  const cargar = async (sec) => {
    setCargando(true);
    try {
      const q = sec ? `?section_id=${sec}` : '';
      const [t, d, p] = await Promise.all([
        affiliationAPI.qroDashboard(sec || undefined),
        affiliationAPI.directory(q),
        affiliationAPI.pagos(sec || undefined),
      ]);
      setTablero(t);
      setDirectorio(d);
      setPagos(p);
    } catch (e) { err(e); } finally { setCargando(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const c = await orgAPI.myContext();
        setCtx(c);
        const inicial = c.accessible_scope === 'ALL' ? '' : (c.accessible_sections?.[0]?.id || '');
        setSeccion(inicial);
        await cargar(inicial);
      } catch (e) { err(e); setCargando(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirBucket = async (key) => {
    try {
      const params = new URLSearchParams({ bucket: key });
      if (seccion) params.append('section_id', seccion);
      const lista = await affiliationAPI.adminRequests(`?${params.toString()}`);
      setBucket(key);
      setSolicitudes(lista);
      setDetalle(null);
    } catch (e) { err(e); }
  };

  const abrirDetalle = async (id) => {
    try {
      const [c, dd] = await Promise.all([affiliationAPI.activationCheck(id), affiliationAPI.docs(id)]);
      setCheck(c);
      setDocs(dd);
      setDetalle(id);
    } catch (e) { err(e); }
  };

  const activar = async (id, conExcepcion) => {
    try {
      const r = await affiliationAPI.activate(id, conExcepcion ? motivo : null);
      toast({ title: 'Afiliado activo', description: `Vigencia hasta ${new Date(r.vigencia_hasta).toLocaleDateString('es-MX')}` });
      setMotivo('');
      await abrirDetalle(id);
      await cargar(seccion);
      if (bucket) await abrirBucket(bucket);
    } catch (e) { err(e); }
  };

  const buscarDirectorio = async () => {
    try {
      const params = new URLSearchParams();
      if (seccion) params.append('section_id', seccion);
      if (filtroEstado) params.append('estado', filtroEstado);
      if (busqueda) params.append('q', busqueda);
      setDirectorio(await affiliationAPI.directory(`?${params.toString()}`));
    } catch (e) { err(e); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-afiliaciones">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Afiliaciones</h1>
            <p className="text-base text-gray-600">
              Solicitudes, expedientes, directorio y pagos {seccion ? 'de tu sección' : 'de todas las secciones'}
            </p>
          </div>
          {ctx?.accessible_scope === 'ALL' && (
            <select data-testid="filtro-seccion" className="rounded-md border border-gray-300 p-2"
              value={seccion} onChange={(e) => { setSeccion(e.target.value); cargar(e.target.value); }}>
              <option value="">Consolidado nacional</option>
              {(ctx.accessible_sections || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.abbreviation})</option>
              ))}
            </select>
          )}
        </div>

        {cargando ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="tablero-cards">
              {(tablero?.cards || []).map((c) => (
                <button key={c.key} data-testid={`card-${c.key}`} onClick={() => abrirBucket(c.key)}
                  className={`text-left rounded-xl border p-4 transition-colors hover:border-gray-900 ${bucket === c.key ? 'border-gray-900 bg-white' : 'border-gray-200 bg-white'}`}>
                  <p className="text-sm text-gray-600">{c.label}</p>
                  <p className="text-3xl font-bold text-gray-900" data-testid={`card-count-${c.key}`}>{c.count}</p>
                  <p className="text-xs text-gray-500 mt-1">Ver listado exacto</p>
                </button>
              ))}
            </div>

            {bucket && (
              <Card data-testid="listado-bucket">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" /> Listado: {bucket} ({solicitudes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {solicitudes.length === 0 && <p className="text-sm text-gray-500">Sin solicitudes en esta tarjeta</p>}
                  {solicitudes.map((s) => (
                    <div key={s.id} data-testid={`solicitud-${s.folio}`}
                      className="flex items-center justify-between border rounded-lg p-3">
                      <div>
                        <p className="font-semibold text-gray-900">{s.folio} — {s.full_name}</p>
                        <p className="text-xs text-gray-600">
                          {s.email} · estado: {s.status} · responsable: {s.responsible_id || 'sin asignar'} ·
                          creada: {s.created_at ? new Date(s.created_at).toLocaleDateString('es-MX') : '—'}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" data-testid={`ver-${s.folio}`}
                        onClick={() => abrirDetalle(s.id)}>Expediente</Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {detalle && check && (
              <Card data-testid="detalle-solicitud">
                <CardHeader>
                  <CardTitle className="text-lg">Expediente {check.folio}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-3 text-sm">
                    <p>Estado: <strong data-testid="detalle-status">{check.status}</strong></p>
                    <p>Expediente completo:{' '}
                      <strong data-testid="detalle-expediente">{check.expediente_completo ? 'sí' : 'no'}</strong></p>
                    <p>Pago confirmado:{' '}
                      <strong data-testid="detalle-pago">{check.pago_confirmado ? 'sí' : 'no'}</strong></p>
                  </div>
                  {check.documentos_faltantes?.length > 0 && (
                    <p data-testid="detalle-faltantes" className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Falta: {check.documentos_faltantes.join(', ')}
                    </p>
                  )}
                  <div>
                    <p className="text-sm font-semibold">Documentos cargados</p>
                    <ul className="text-sm text-gray-700 list-disc pl-5" data-testid="detalle-docs">
                      {docs.length === 0 && <li>Sin documentos</li>}
                      {docs.map((d) => <li key={d.id}>{d.doc_type} — {d.review_status || 'pendiente'}</li>)}
                    </ul>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <Button className="bg-gray-900" disabled={!check.puede_activar}
                      data-testid="activar-btn" onClick={() => activar(detalle, false)}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {check.puede_activar ? 'Activar afiliado' : 'Requisitos incompletos'}
                    </Button>
                    <div className="flex items-end gap-2">
                      <div>
                        <label className="text-xs text-gray-600">Excepción autorizada (motivo)</label>
                        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                          placeholder="Ej. pago en efectivo, recibo 001" data-testid="excepcion-motivo" />
                      </div>
                      <Button variant="outline" disabled={!motivo || !check.expediente_completo}
                        data-testid="activar-excepcion-btn" onClick={() => activar(detalle, true)}>
                        Activar por excepción
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Criterio: solicitud aprobada + expediente completo + pago confirmado (${check.cuota_vigente} MXN)
                    o excepción autorizada y auditada.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card data-testid="directorio">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" /> Directorio de afiliados ({directorio?.total || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {['', 'activo', 'por_renovar', 'pendiente', 'vencido'].map((e) => (
                    <Button key={e || 'todos'} size="sm" variant={filtroEstado === e ? 'default' : 'outline'}
                      className={filtroEstado === e ? 'bg-gray-900' : ''}
                      data-testid={`dir-filtro-${e || 'todos'}`}
                      onClick={() => { setFiltroEstado(e); setTimeout(buscarDirectorio, 0); }}>
                      {e === '' ? 'Todos' : e.replace('_', ' ')}
                      {directorio?.resumen && e !== '' ? ` (${directorio.resumen[e] ?? 0})` : ''}
                    </Button>
                  ))}
                  <div className="flex gap-2 ml-auto">
                    <Input placeholder="Buscar nombre, correo o folio" value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)} data-testid="dir-busqueda" className="w-64" />
                    <Button variant="outline" onClick={buscarDirectorio} data-testid="dir-buscar">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {(directorio?.items || []).slice(0, 50).map((a) => (
                    <div key={a.request_id} data-testid={`dir-item-${a.folio}`}
                      className="flex items-center justify-between border rounded-lg p-3">
                      <div>
                        <p className="font-semibold text-gray-900">{a.nombre}</p>
                        <p className="text-xs text-gray-600">
                          {a.folio} · {a.email} · vigencia: {a.vigencia_hasta ? new Date(a.vigencia_hasta).toLocaleDateString('es-MX') : '—'}
                          {a.por_excepcion ? ' · activado por excepción' : ''}
                        </p>
                      </div>
                      <Badge className={ESTADO_BADGE[a.estado]}>{a.estado.replace('_', ' ')}</Badge>
                    </div>
                  ))}
                  {(directorio?.items || []).length === 0 && (
                    <p className="text-sm text-gray-500" data-testid="dir-vacio">Sin afiliados con ese criterio</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="pagos">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> Pagos ({pagos?.total_registros || 0}) — modo {pagos?.modo}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-gray-700 flex flex-wrap items-center gap-3" data-testid="pagos-total">
                  <span>Confirmado: <strong>${(pagos?.total_confirmado || 0).toLocaleString('es-MX')} {pagos?.moneda}</strong></span>
                  {(pagos?.en_revision || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs"
                          data-testid="pagos-en-revision">
                      <AlertTriangle className="h-3 w-3" /> {pagos.en_revision} en revisión
                    </span>
                  )}
                  <Link to="/admin/renovaciones" className="text-sm underline text-blue-700"
                        data-testid="enlace-renovaciones">
                    Renovaciones y conciliación →
                  </Link>
                </p>
                {(pagos?.items || []).slice(0, 30).map((p) => (
                  <div key={p.id} data-testid={`pago-${p.folio}`}
                    className="flex items-center justify-between border rounded-lg p-3 text-sm">
                    <span>{p.folio} — {p.solicitante} · {p.concepto}</span>
                    <span className="flex items-center gap-3">
                      <strong>${p.amount?.toLocaleString('es-MX')} {p.currency}</strong>
                      <Badge className={p.estado === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>
                        {p.estado}
                      </Badge>
                    </span>
                  </div>
                ))}
                {(pagos?.items || []).length === 0 && (
                  <p className="text-sm text-gray-500" data-testid="pagos-vacio">Sin pagos registrados</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAfiliaciones;
